import { useCheckoutInfoContext } from "pages/checkout/providers/CheckoutInfoProvider";
import { isValidPhoneNumber } from "react-phone-number-input";
import { isValidEmailAddress } from "shared/utils/validation";
import { CustomerTypes } from "app/hooks/";
import { createContext, useContext, useEffect, useState } from "react";

export type InfoFormErrors = {
  name: string;
  surname: string;
  email: string;
  phoneNumber: string;
  type: string;
  companyName: string;
  companyPhoneNumber: string;
  shippingPhone: string;
  houseNumber: string;
  zipCode: string;
  street: string;
  city: string;

  contactFirstName: string;
  contactLastName: string;
  contactPhoneNumber: string;
  contactEmail: string;

  invoiceZipCode: string;
  invoiceHouseNumber: string;

  agreedOnTerms: string;
  vatNumber: string;
  btwNumber: string;
};

const initialFormErrorsState: InfoFormErrors = {
  name: "",
  surname: "",
  email: "",
  phoneNumber: "",
  shippingPhone: "",
  companyPhoneNumber: "",
  type: "",
  companyName: "",
  houseNumber: "",
  zipCode: "",
  street: "",
  city: "",
  contactFirstName: "",
  contactLastName: "",
  contactPhoneNumber: "",
  contactEmail: "",
  invoiceZipCode: "",
  invoiceHouseNumber: "",
  agreedOnTerms: "",
  vatNumber: "",
  btwNumber: "",
};

interface InfoFormErrorsProviderValues {
  getFormErrors: () => InfoFormErrors;
  formErrors: InfoFormErrors;
  setFormErrors: React.Dispatch<React.SetStateAction<InfoFormErrors>>;
  isFormValid: () => boolean;
}

const Context = createContext({} as InfoFormErrorsProviderValues);
export const useInfoFormErrorsContext = () => useContext(Context);

export const InfoFormErrorsProvider = ({ children }: any) => {
  const { state } = useCheckoutInfoContext();
  const [formErrors, setFormErrors] = useState<InfoFormErrors>({
    ...initialFormErrorsState,
  });

  useEffect(() => {
    if (!state.isSubmitting) return;

    const errors = getFormErrors();
    setFormErrors(errors);
  }, [state]);

  const isFormValid = () => {
    let isValid = true;

    const errors = getFormErrors();
    Object.keys(errors).map((key) => {
      if (errors[key as keyof typeof formErrors].length !== 0) {
        isValid = false;
      }
    });

    return isValid;
  };

  const getFormErrors = () => {
    const errors: InfoFormErrors = { ...initialFormErrorsState };

    if (state.type === CustomerTypes.PERSONAL) {
      if (!state.name) errors.name = "Voornaam mag niet leeg zijn";
      if (!state.surname) errors.surname = "Achternaam mag niet leeg zijn";
      if (!state.email) errors.email = "E-mail mag niet leeg zijn";
      if (state.email && !isValidEmailAddress(state.email))
        errors.email = "U moet een geldig e-mailadres invullen.";
      if (!state.phoneNumber || !isValidPhoneNumber(state.phoneNumber))
        errors.phoneNumber = "Telefoonnummer is niet geldig";
    } else if (state.type === CustomerTypes.BUSINESS) {
      if (!state.companyName)
        errors.companyName = "Bedrijfsnaam mag niet leeg zijn";
      if (!state.contactFirstName)
        errors.contactFirstName =
          "Voornaam van contactpersoon mag niet leeg zijn";
      if (!state.contactLastName)
        errors.contactLastName =
          "Achternaam van contactpersoon mag niet leeg zijn";
      if (
        !state.companyPhoneNumber ||
        !isValidPhoneNumber(state.companyPhoneNumber)
      )
        errors.companyPhoneNumber = "Telefoonnummer is niet geldig";
      if (!state.contactEmail)
        errors.contactEmail = "Email van contactpersooon mag niet leeg zijn";
      if (state.contactEmail && !isValidEmailAddress(state.contactEmail))
        errors.contactEmail = "U moet een geldig e-mailadres invullen.";

      // KVK, BTW errors
      if (!state.vatNumber) errors.vatNumber = "KVK nummer mag niet leeg zijn";
    }

    if (!state.zipCode) errors.zipCode = "Postcode mag niet leeg zijn";
    if (state.zipCode && state.zipCode.replace(" ", "").length !== 6)
      errors.zipCode = "U moet een geldig adres invullen";
    if (!state.houseNumber)
      errors.houseNumber = "Huisnummer mag niet leeg zijn";
    if (!state.street) errors.street = "Adres is niet gevonden";
    if (!state.city) errors.city = "Woonplaats is niet gevonden";

    if (!state.isSameAddress) {
      if (!state.invoiceZipCode)
        errors.invoiceZipCode = "Postcode mag niet leeg zijn";
      if (
        state.invoiceZipCode &&
        state.invoiceZipCode.replace(" ", "").length !== 6
      )
        errors.invoiceZipCode = "U moet een geldig adres invullen";
      if (!state.invoiceHouseNumber)
        errors.invoiceHouseNumber = "Huisnummer mag niet leeg zijn";
    }

    if (!state.agreedOnTerms)
      errors.agreedOnTerms =
        "Je moet nog akkoord gaan met onze algemene voorwaarden om verder te gaan";

    return errors;
  };

  return (
    <Context.Provider
      value={{ getFormErrors, formErrors, setFormErrors, isFormValid }}
    >
      {children}
    </Context.Provider>
  );
};
